import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getToolById, deleteTool } from '../toolService';
import { ChevronLeft, Trash2 } from 'lucide-react';
import DocumentationViewer from './DocumentationViewer';

const ToolDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [tool, setTool] = useState(null);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({});
    const [response, setResponse] = useState(null);
    const [testLoading, setTestLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('documentation');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);


    useEffect(() => {
        const fetchToolDetails = async () => {
            try {
                const toolData = await getToolById(id);
                if (toolData) {
                    setTool(toolData);

                    // Initialize form data based on tool's body configuration
                    if (toolData.body_mode === 'formdata' && toolData.formdata) {
                        try {
                            const parsedFormData = JSON.parse(toolData.formdata);
                            const initialFormData = {};

                            if (parsedFormData.formdata && Array.isArray(parsedFormData.formdata)) {
                                parsedFormData.formdata.forEach(item => {
                                    initialFormData[item.key] = item.value || '';
                                });
                            }

                            setFormData(initialFormData);
                        } catch (error) {
                            console.error('Error parsing formdata:', error);
                        }
                    } else if (toolData.body_mode === 'raw' && toolData.raw_body) {
                        setFormData({ rawBody: toolData.raw_body });
                    }
                }
                setLoading(false);
            } catch (error) {
                console.error('Error fetching tool details:', error);
                setLoading(false);
            }
        };

        fetchToolDetails();
    }, [id]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e, key) => {
        const file = e.target.files[0];
        setFormData(prev => ({ ...prev, [key]: file }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setTestLoading(true);
        setResponse(null);

        try {
            let requestBody;
            let headers = {};

            // Parse headers if available
            if (tool.headers) {
                try {
                    const parsedHeaders = JSON.parse(tool.headers);
                    if (Array.isArray(parsedHeaders)) {
                        parsedHeaders.forEach(header => {
                            if (!header.disabled) {
                                headers[header.key] = header.value;
                            }
                        });
                    }
                } catch (error) {
                    console.error('Error parsing headers:', error);
                }
            }

            // Prepare request body based on body mode
            if (tool.body_mode === 'formdata') {
                requestBody = new FormData();
                Object.entries(formData).forEach(([key, value]) => {
                    if (value instanceof File) {
                        requestBody.append(key, value);
                    } else if (value) {
                        requestBody.append(key, value);
                    }
                });
            } else if (tool.body_mode === 'raw') {
                requestBody = formData.rawBody;
                // If Content-Type is application/json, parse the raw body
                if (headers['Content-Type'] === 'application/json') {
                    try {
                        requestBody = JSON.parse(formData.rawBody);
                    } catch (error) {
                        console.error('Error parsing JSON body:', error);
                    }
                }
            }

            // Make the API request
            const response = await fetch(tool.url, {
                method: tool.method,
                headers,
                body: requestBody,
            });

            const contentType = response.headers.get('content-type');
            let responseData;

            if (contentType && contentType.includes('application/json')) {
                responseData = await response.json();
            } else if (contentType && contentType.includes('text/html')) {
                responseData = await response.text();
            } else if (contentType && contentType.includes('text/event-stream')) {
                // Handle SSE responses
                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let events = [];

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    const chunk = decoder.decode(value);
                    const eventLines = chunk.split('\n\n');

                    for (const eventLine of eventLines) {
                        if (eventLine.trim()) {
                            const eventData = eventLine.split('\n')
                                .filter(line => line.startsWith('data:'))
                                .map(line => line.substring(5).trim())
                                .join('');

                            if (eventData) {
                                try {
                                    const parsedEvent = JSON.parse(eventData);
                                    events.push(parsedEvent);
                                } catch (e) {
                                    events.push({ raw: eventData });
                                }
                            }
                        }
                    }
                }

                responseData = { events };
            } else {
                responseData = await response.text();
            }

            setResponse({
                status: response.status,
                statusText: response.statusText,
                data: responseData,
                contentType
            });
        } catch (error) {
            setResponse({
                error: true,
                message: error.message
            });
        } finally {
            setTestLoading(false);
        }
    };

    const handleDeleteTool = async () => {
        setDeleteLoading(true);
        try {
            const success = await deleteTool(id);

            if (success) {
                // Use window.location for a hard redirect instead of React Router
                window.location.href = '/marketplace';
            } else {
                throw new Error("Failed to delete tool");
            }
        } catch (error) {
            console.error("Error deleting tool:", error);
            setShowDeleteModal(false);
        } finally {
            setDeleteLoading(false);
        }
    };


    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (!tool) {
        return (
            <div className="max-w-7xl mx-auto px-4 py-8">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <strong className="font-bold">Error!</strong>
                    <span className="block sm:inline"> Tool not found.</span>
                </div>
                <button
                    onClick={() => navigate('/marketplace')}
                    className="mt-4 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                >
                    Back to Marketplace
                </button>
            </div>
        );
    }

    const renderResponseDisplay = () => {
        if (!response) return null;

        if (response.error) {
            return (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mt-4">
                    <strong className="font-bold">Error:</strong>
                    <span className="block sm:inline"> {response.message}</span>
                </div>
            );
        }

        return (
            <div className="mt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Response</h3>
                <div className="bg-gray-100 p-4 rounded-md">
                    <div className="mb-2">
                        <span className="font-semibold">Status:</span>
                        <span className={response.status >= 200 && response.status < 300 ? "text-green-600" : "text-red-600"}>
                            {response.status} {response.statusText}
                        </span>
                    </div>

                    {response.contentType && response.contentType.includes('application/json') ? (
                        <pre className="bg-gray-800 text-white p-4 rounded-md overflow-x-auto">
                            {JSON.stringify(response.data, null, 2)}
                        </pre>
                    ) : response.contentType && response.contentType.includes('text/html') ? (
                        <div className="border border-gray-300 rounded-md p-2 mt-2">
                            <iframe
                                srcDoc={response.data}
                                title="HTML Response"
                                className="w-full h-96 border-0"
                                sandbox="allow-scripts"
                            />
                        </div>
                    ) : (
                        <pre className="bg-gray-800 text-white p-4 rounded-md overflow-x-auto">
                            {response.data}
                        </pre>
                    )}
                </div>
            </div>
        );
    };

    const renderDocumentationTab = () => {
        return (
            <div className="p-6">
                <DocumentationViewer markdown={tool.full_description || 'No documentation available.'} />

                {/* Request Description */}
                {tool.request_description && (
                    <div className="mt-8">
                        <h3 className="text-xl font-semibold mt-5 mb-2 text-gray-800">Request</h3>
                        <p className="my-4 text-gray-700 leading-relaxed">{tool.request_description}</p>
                    </div>
                )}

                {/* Response Description */}
                {tool.response_description && (
                    <div className="mt-6">
                        <h3 className="text-xl font-semibold mt-5 mb-2 text-gray-800">Response</h3>
                        <p className="my-4 text-gray-700 leading-relaxed">{tool.response_description}</p>
                    </div>
                )}

                {/* Example Response */}
                {tool.example_response && (
                    <div className="mt-6">
                        <h3 className="text-xl font-semibold mt-5 mb-2 text-gray-800">Example Response</h3>
                        <div className="rounded-md overflow-hidden my-6 bg-gray-800">
                            <div className="px-4 py-1 text-xs text-gray-400 uppercase bg-gray-900">
                                Response
                            </div>
                            <pre className="p-4 overflow-x-auto text-white">
                                {tool.example_response}
                            </pre>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Render "Try It" tab content
    const renderTryItTab = () => {
        let formFields = [];

        if (tool.body_mode === 'formdata' && tool.formdata) {
            try {
                const parsedFormData = JSON.parse(tool.formdata);
                if (parsedFormData.formdata && Array.isArray(parsedFormData.formdata)) {
                    formFields = parsedFormData.formdata;
                }
            } catch (error) {
                console.error('Error parsing formdata:', error);
            }
        }

        return (
            <div>
                <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Request Details</h3>
                    <div className="bg-gray-100 p-4 rounded-md">
                        <div className="mb-2">
                            <span className="font-semibold">Method:</span>
                            <span className={`ml-2 px-2 py-1 text-xs font-medium rounded ${tool.method === 'GET' ? 'bg-blue-100 text-primaryDark' :
                                    tool.method === 'POST' ? 'bg-green-100 text-green-800' :
                                        'bg-purple-100 text-purple-800'
                                }`}>
                                {tool.method}
                            </span>
                        </div>
                        <div className="mb-2">
                            <span className="font-semibold">URL:</span>
                            <span className="ml-2 text-gray-600 break-all">{tool.url}</span>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {tool.body_mode === 'formdata' && formFields.length > 0 && (
                        <div>
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Request Parameters</h3>
                            <div className="space-y-4">
                                {formFields.map((param, index) => (
                                    <div key={index} className="grid grid-cols-1 gap-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            {param.key} {param.description && <span className="text-xs text-gray-500">({param.description})</span>}
                                        </label>
                                        {param.type === 'file' ? (
                                            <input
                                                type="file"
                                                onChange={(e) => handleFileChange(e, param.key)}
                                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                                            />
                                        ) : (
                                            <input
                                                type="text"
                                                name={param.key}
                                                value={formData[param.key] || ''}
                                                onChange={handleInputChange}
                                                placeholder={param.value || ''}
                                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {tool.body_mode === 'raw' && (
                        <div>
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Request Body</h3>
                            <textarea
                                name="rawBody"
                                value={formData.rawBody || ''}
                                onChange={handleInputChange}
                                rows={10}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono"
                            />
                        </div>
                    )}

                    <div>
                        <button
                            type="submit"
                            disabled={testLoading}
                            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                        >
                            {testLoading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Processing...
                                </>
                            ) : (
                                'Send Request'
                            )}
                        </button>
                    </div>
                </form>

                {renderResponseDisplay()}
            </div>
        );
    };


    return (
        <div className="bg-gray-50 min-h-screen py-8">
            <div className="max-w-7xl mx-auto px-4">
                {/* Tool header */}
                <div className="bg-white p-6 rounded-xl shadow-md mb-6 group">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <button
                                className="text-gray-600 hover:text-gray-800 transition-colors"
                                onClick={() => navigate('/marketplace')}
                            >
                                <ChevronLeft className="w-6 h-6" />
                            </button>
                            <div className="text-left">
                                <h1 className="text-2xl font-medium text-primary">{tool?.name}</h1>
                                <p className="text-gray-600">{tool?.description}</p>
                            </div>
                        </div>
                        {sessionStorage.getItem('role') === 'admin' && (
                            <button
                                onClick={() => setShowDeleteModal(true)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete Tool
                            </button>
                        )}
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="border-b border-gray-200">
                        <nav className="flex -mb-px">
                            <button
                                className={`py-4 px-6 font-medium text-sm border-b-2 ${activeTab === 'documentation'
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                onClick={() => setActiveTab('documentation')}
                            >
                                Documentation
                            </button>
                            <button
                                className={`py-4 px-6 font-medium text-sm border-b-2 ${activeTab === 'try-it'
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                onClick={() => setActiveTab('try-it')}
                            >
                                Try It
                            </button>
                        </nav>
                    </div>

                    <div className="p-6">
                        {activeTab === 'documentation' && renderDocumentationTab()}
                        {activeTab === 'try-it' && renderTryItTab()}
                    </div>
                </div>

                {/* Delete confirmation modal */}
                {showDeleteModal && (
                    <div className="fixed inset-0 flex items-center justify-center z-50 backdrop-blur-sm">
                        <div className="bg-white rounded-lg p-6 max-w-md w-full">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Tool</h3>
                            <p className="text-gray-500 mb-6">
                                Are you sure you want to delete "{tool.name}"? This action cannot be undone.
                            </p>
                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => setShowDeleteModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                    disabled={deleteLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteTool}
                                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                    disabled={deleteLoading}
                                >
                                    {deleteLoading ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Deleting...
                                        </>
                                    ) : (
                                        'Delete'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ToolDetailPage;

